import { Line, Text } from '@react-three/drei';
import { Box3, Vector3 } from 'three';

interface BoundingBoxProps {
  box: Box3 | null; // Box3 from THREE, or null if no box is present
  boundingBoxColor: string;
  dTextColor: string;
  unit: string;
}

function BoundingBox({
  box,
  boundingBoxColor,
  dTextColor,
  unit,
}: BoundingBoxProps) {
  if (!box) return null;

  const width = box.max.x - box.min.x;
  const depth = box.max.y - box.min.y;
  const height = box.max.z - box.min.z;

  // Increase text size for better visibility
  const textSize = Math.max(width, depth, height) * 0.05; // Adjusted text size relative to dimensions

  // Get center of the bounding box for proper centering
  const boxCenter = new Vector3();
  box.getCenter(boxCenter);

  // Adjust bounding box points to be centered relative to the origin
  const boxPoints: Vector3[] = [
    new Vector3(box.min.x, box.min.y, box.min.z).sub(boxCenter),
    new Vector3(box.max.x, box.min.y, box.min.z).sub(boxCenter),
    new Vector3(box.max.x, box.max.y, box.min.z).sub(boxCenter),
    new Vector3(box.min.x, box.max.y, box.min.z).sub(boxCenter),
    new Vector3(box.min.x, box.min.y, box.min.z).sub(boxCenter),
    new Vector3(box.min.x, box.min.y, box.max.z).sub(boxCenter),
    new Vector3(box.max.x, box.min.y, box.max.z).sub(boxCenter),
    new Vector3(box.max.x, box.max.y, box.max.z).sub(boxCenter),
    new Vector3(box.min.x, box.max.y, box.max.z).sub(boxCenter),
    new Vector3(box.min.x, box.min.y, box.max.z).sub(boxCenter),
    new Vector3(box.min.x, box.max.y, box.max.z).sub(boxCenter),
    new Vector3(box.min.x, box.max.y, box.min.z).sub(boxCenter),
    new Vector3(box.max.x, box.max.y, box.min.z).sub(boxCenter),
    new Vector3(box.max.x, box.max.y, box.max.z).sub(boxCenter),
    new Vector3(box.max.x, box.min.y, box.max.z).sub(boxCenter),
    new Vector3(box.max.x, box.min.y, box.min.z).sub(boxCenter),
  ];

  // Offset for placing the text labels
  const labelOffset = 0.2; // Increase offset for improved visibility

  return (
    <>
      {/* Draw the bounding box */}
      <Line
        points={boxPoints.map((p) => [p.x, p.y, p.z])}
        color={boundingBoxColor}
        lineWidth={1}
      />

      {/* Width label - place it above the bounding box line */}
      <Text
        position={[
          (box.min.x + box.max.x) / 2 - boxCenter.x,
          box.min.y - boxCenter.y - labelOffset,
          box.max.z - boxCenter.z + labelOffset,
        ]}
        fontSize={textSize}
        color={dTextColor}
      >
        {`${width.toFixed(2)} ${unit}`}
      </Text>

      {/* Depth label - place it to the side and above the bounding box */}
      <Text
        position={[
          box.max.x - boxCenter.x + labelOffset,
          (box.min.y + box.max.y) / 2 - boxCenter.y,
          box.max.z - boxCenter.z + labelOffset,
        ]}
        fontSize={textSize}
        color={dTextColor}
        rotation={[0, 0, Math.PI / 2]}
      >
        {`${depth.toFixed(2)} ${unit}`}
      </Text>

      {/* Height label - place it on the top edge */}
      <Text
        position={[
          box.max.x - boxCenter.x,
          box.max.y - boxCenter.y + labelOffset,
          (box.min.z + box.max.z) / 2 - boxCenter.z,
        ]}
        fontSize={textSize}
        color={dTextColor}
        rotation={[Math.PI / 2, 0, 0]}
      >
        {`${height.toFixed(2)} ${unit}`}
      </Text>
    </>
  );
}

export default BoundingBox;
